import { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { WhatsappLogo, Copy, UserCheck, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/fmt";
import { toWhatsAppNumber } from "@/lib/contact";
import ReferenceBadge from "@/components/ReferenceBadge";
import { useBranding } from "@/contexts/BrandingContext";

/**
 * Post-convert communication modal.
 *
 * Shown after admin successfully converts a guest ride-request. Displays:
 *  - Account status badge: "Compte client créé" / "Client existant lié" / skipped note
 *  - A ready-to-use message (booking details + optional password-setup link)
 *  - Two admin actions using the SAME message body:
 *      • Envoyer par WhatsApp  → opens wa.me/<phone>?text=<message>
 *      • Copier pour email      → copies the same text to the clipboard
 *
 * The system does NOT auto-send anything; admin is in full control.
 */
export default function ClientAccountShareModal({ open, onClose, data, ride }) {
  const account = data?.client_account || null;
  const branding = useBranding();   // company name for outbound client messages

  const message = useMemo(() => {
    if (!open || !ride) return "";
    const ref = ride.reference || `#${(ride.id || "").slice(0, 8).toUpperCase()}`;
    const price = ride.admin_price != null ? ride.admin_price : ride.driver_fare;
    // Phase 33.3 — outbound message uses the active tenant name instead of
    // the hardcoded S.DRIVE brand so client communications stay white-label.
    const brand = branding.companyName || "S.DRIVE";
    const lines = [
      `Bonjour ${ride.client_name || ""},`,
      "",
      `Votre réservation ${brand} a bien été confirmée :`,
      "",
      `• Référence : ${ref}`,
      `• Départ : ${ride.pickup_location || ride.route_label?.split("→")[0]?.trim() || "—"}`,
      `• Arrivée : ${ride.dropoff_location || ride.route_label?.split("→")[1]?.trim() || "—"}`,
      `• Date : ${fmtDate(ride.pickup_date)} à ${ride.pickup_time}`,
      `• Tarif : ${price} EUR`,
    ];
    if (ride.is_round_trip && ride.return_date) {
      lines.push(`• Retour : ${fmtDate(ride.return_date)} à ${ride.return_time}`);
    }
    lines.push("");
    if (account?.status === "created") {
      lines.push(`Un espace client a été créé pour vous sur ${brand}.`);
      lines.push(`Email : ${account.email}`);
      if (account.setup_link) {
        lines.push("");
        lines.push("Pour définir votre mot de passe et suivre vos courses, cliquez sur le lien ci-dessous (valable 7 jours) :");
        lines.push(account.setup_link);
      }
    } else if (account?.status === "existing") {
      lines.push(`Votre demande a été rattachée à votre espace client ${brand} existant.`);
      lines.push(`Connectez-vous avec votre email : ${account.email}`);
    }
    lines.push("");
    lines.push("Pour toute question, n'hésitez pas à nous contacter.");
    lines.push(`— L'équipe ${brand}`);
    return lines.join("\n");
  }, [open, ride, account, branding.companyName]);

  const phone = ride?.client_phone || "";
  const waNumber = toWhatsAppNumber(phone);

  const sendWhatsApp = () => {
    if (!waNumber) {
      toast.error("Numéro de téléphone du client manquant");
      return;
    }
    const url = `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyForEmail = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Message copié — collez-le dans votre email");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = message;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("Message copié — collez-le dans votre email");
    }
  };

  if (!account) return null;

  const statusLabel =
    account.status === "created"
      ? { text: "Compte client créé", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", Icon: UserPlus }
      : account.status === "existing"
      ? { text: "Client existant lié", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30", Icon: UserCheck }
      : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="client-share-modal" className="bg-[#121212] border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Contacter le client</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Course confirmée — envoyez la confirmation au client via WhatsApp ou copiez pour un email.
          </DialogDescription>
        </DialogHeader>

        {ride?.reference && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Référence</span>
            <ReferenceBadge value={ride.reference} size="sm" testid="client-share-ref-badge" />
          </div>
        )}

        {statusLabel && (
          <div data-testid={`client-share-status-${account.status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs font-medium ${statusLabel.cls} self-start`}>
            <statusLabel.Icon size={14} weight="fill" /> {statusLabel.text}
          </div>
        )}
        {account.status === "skipped" && (
          <p className="text-xs text-zinc-500 italic">Aucun email client — impossible de créer un compte automatiquement.</p>
        )}

        {account.status !== "skipped" && (
          <>
            <div className="space-y-1 text-xs text-zinc-500">
              {account.email && <div><span className="uppercase tracking-wider mr-1">Email client :</span><span className="text-zinc-200">{account.email}</span></div>}
              {phone && <div><span className="uppercase tracking-wider mr-1">Téléphone :</span><span className="text-zinc-200">{phone}</span></div>}
              {typeof account.linked_requests === "number" && account.linked_requests > 0 && (
                <div><span className="uppercase tracking-wider mr-1">Demandes rattachées :</span><span className="text-zinc-200">{account.linked_requests}</span></div>
              )}
            </div>

            <pre data-testid="client-share-message" className="bg-[#0a0a0a] border border-zinc-800 rounded-lg p-4 text-sm text-zinc-200 whitespace-pre-wrap font-mono leading-relaxed max-h-[45vh] overflow-y-auto">{message}</pre>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                data-testid="client-share-whatsapp"
                onClick={sendWhatsApp}
                className="flex-1 bg-[#25D366] text-black font-semibold hover:bg-[#1ea856] h-11"
                disabled={!waNumber}
                title={!waNumber ? "Téléphone client manquant" : "Ouvrir WhatsApp avec le message pré-rempli"}
              >
                <WhatsappLogo size={18} weight="fill" className="mr-2" /> Envoyer par WhatsApp
              </Button>
              <Button
                data-testid="client-share-copy"
                onClick={copyForEmail}
                variant="outline"
                className="flex-1 border-amber-500/40 text-amber-400 hover:bg-amber-500/10 h-11"
              >
                <Copy size={18} className="mr-2" /> Copier pour email
              </Button>
            </div>

            <p className="text-[11px] text-zinc-500 text-center">
              Aucun email n'est envoyé automatiquement — vous contrôlez entièrement la communication avec le client.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
